import { useCallback, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useNotification } from '../contexts/NotificationContext'
import { insertTradeComment } from '../lib/tradeComments'
import { updateTradeQuantityAfterEvent } from '../lib/tradeClose'
import { supabase } from '../lib/supabase'
import type { Trade } from '../types/trade'

export type TradeQuickModalKind = 'sltp' | 'partialClose' | 'partialAdd' | 'fullExit' | 'note'

interface TradeListQuickActionsProps {
  trade: Trade
  onUpdated: () => void
  /** Studio shell: open in-app editor instead of classic /app/edit-trade route */
  onEditTrade?: (trade: Trade) => void
  /** Opens the given modal immediately without rendering the menu trigger (e.g. feed action links). */
  directAction?: TradeQuickModalKind | null
  onDirectClose?: () => void
}

const MENU_GAP = 8
const MENU_MIN_WIDTH = 228
/** Approximate menu height (~9 items); menu also gets max-height + scroll if needed */
const MENU_EST_HEIGHT = 340
const VIEW_PAD = 8

/** Keep dropdown fully inside the viewport; flip above anchor when bottom rows leave no space below */
function computeMenuPosition(rect: DOMRect): { top: number; left: number } {
  if (typeof window === 'undefined') return { top: rect.bottom + MENU_GAP, left: rect.right - MENU_MIN_WIDTH }
  const vw = window.innerWidth
  const vh = window.innerHeight

  const spaceBelow = vh - rect.bottom - MENU_GAP
  const spaceAbove = rect.top - MENU_GAP

  let top =
    spaceBelow >= MENU_EST_HEIGHT || spaceBelow >= spaceAbove
      ? rect.bottom + MENU_GAP
      : rect.top - MENU_EST_HEIGHT - MENU_GAP

  top = Math.min(Math.max(top, VIEW_PAD), Math.max(VIEW_PAD, vh - MENU_EST_HEIGHT - VIEW_PAD))

  let left = rect.right - MENU_MIN_WIDTH
  left = Math.min(Math.max(left, VIEW_PAD), Math.max(VIEW_PAD, vw - MENU_MIN_WIDTH - VIEW_PAD))

  return { top, left }
}

/** Last/mark price for partial add/close, then entry as fallback */
function defaultPartialEventPrice(trade: Trade): string {
  const last =
    trade.current_price != null && Number.isFinite(trade.current_price)
      ? trade.current_price
      : trade.previous_close_price != null && Number.isFinite(trade.previous_close_price)
        ? trade.previous_close_price
        : trade.entry_price
  return last != null ? String(last) : ''
}

export default function TradeListQuickActions({
  trade,
  onUpdated,
  onEditTrade,
  directAction = null,
  onDirectClose,
}: TradeListQuickActionsProps) {
  const { t } = useTranslation(['trading', 'common'])
  const { user } = useAuth()
  const navigate = useNavigate()
  const { showError, showSuccess, showConfirm } = useNotification()

  const [menuAnchor, setMenuAnchor] = useState<{ rect: DOMRect } | null>(null)
  const [modal, setModal] = useState<{ kind: TradeQuickModalKind; trade: Trade } | null>(null)
  const [sltpDraft, setSltpDraft] = useState({ sl: '', tp: '' })
  const [partialForm, setPartialForm] = useState({ qty: '', price: '', eventDate: '' })
  const [fullExitForm, setFullExitForm] = useState({ exitPrice: '', exitDate: '' })
  const [noteDraft, setNoteDraft] = useState('')
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const isOpenPosition = !trade.exit_date

  const closeModal = () => {
    setModal(null)
    setSaving(false)
    onDirectClose?.()
  }

  const openAction = (kind: TradeQuickModalKind) => {
    setMenuAnchor(null)
    setModal({ kind, trade })
    const nowLocal = new Date().toISOString().slice(0, 16)
    switch (kind) {
      case 'sltp':
        setSltpDraft({
          sl: trade.stop_loss != null ? String(trade.stop_loss) : '',
          tp: trade.take_profit != null ? String(trade.take_profit) : '',
        })
        break
      case 'partialClose':
      case 'partialAdd':
        setPartialForm({
          qty: '',
          price: defaultPartialEventPrice(trade),
          eventDate: nowLocal,
        })
        break
      case 'fullExit':
        setFullExitForm({
          exitPrice: defaultPartialEventPrice(trade),
          exitDate: nowLocal,
        })
        break
      case 'note':
        setNoteDraft('')
        break
      default:
        break
    }
  }

  useEffect(() => {
    if (!directAction) return
    setMenuAnchor(null)
    setModal({ kind: directAction, trade })
    const nowLocal = new Date().toISOString().slice(0, 16)
    switch (directAction) {
      case 'sltp':
        setSltpDraft({
          sl: trade.stop_loss != null ? String(trade.stop_loss) : '',
          tp: trade.take_profit != null ? String(trade.take_profit) : '',
        })
        break
      case 'partialClose':
      case 'partialAdd':
        setPartialForm({
          qty: '',
          price: defaultPartialEventPrice(trade),
          eventDate: nowLocal,
        })
        break
      case 'fullExit':
        setFullExitForm({
          exitPrice: defaultPartialEventPrice(trade),
          exitDate: nowLocal,
        })
        break
      case 'note':
        setNoteDraft('')
        break
      default:
        break
    }
  }, [directAction, trade])

  useEffect(() => {
    if (!menuAnchor) return
    const close = () => setMenuAnchor(null)
    // Window only â€” capture:false avoids closing when scrolling inside nested overflow (trade list body)
    window.addEventListener('scroll', close, false)
    window.addEventListener('resize', close)
    return () => {
      window.removeEventListener('scroll', close, false)
      window.removeEventListener('resize', close)
    }
  }, [menuAnchor])

  const submitSltp = async () => {
    if (!user || !modal) return
    const slRaw = sltpDraft.sl.trim()
    const tpRaw = sltpDraft.tp.trim()
    const sl = slRaw === '' ? null : parseFloat(slRaw.replace(',', '.'))
    const tp = tpRaw === '' ? null : parseFloat(tpRaw.replace(',', '.'))
    if (sl != null && !Number.isFinite(sl)) {
      showError(t('tradeList.quickErrors.invalidStopLoss'))
      return
    }
    if (tp != null && !Number.isFinite(tp)) {
      showError(t('tradeList.quickErrors.invalidTakeProfit'))
      return
    }
    setSaving(true)
    try {
      const { error } = await supabase
        .from('trades')
        .update({
          stop_loss: sl,
          take_profit: tp,
        })
        .eq('id', modal.trade.id)
      if (error) throw error
      showSuccess(t('tradeList.quickSaved.sltp'))
      closeModal()
      onUpdated()
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : String(e))
    } finally {
      setSaving(false)
    }
  }

  const formatSentenceDate = (local: string) => {
    const d = new Date(local)
    return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })
  }

  const submitPartialEvent = async (kind: 'partialClose' | 'partialAdd') => {
    if (!user || !modal) return
    const event_type = kind === 'partialClose' ? 'reduce_size' : 'add'
    const qtyChg = parseFloat(partialForm.qty.replace(',', '.'))
    const price = parseFloat(partialForm.price.replace(',', '.'))
    if (!Number.isFinite(qtyChg) || qtyChg <= 0) {
      showError(t('tradeList.quickErrors.quantityPositive'))
      return
    }
    if (!Number.isFinite(price)) {
      showError(t('tradeList.quickErrors.priceRequired'))
      return
    }
    const T = modal.trade
    let updatedQuantity = T.quantity ?? 0
    let updatedPositionSize = T.position_size ?? 0

    if (event_type === 'add') {
      updatedQuantity = (T.quantity ?? 0) + qtyChg
      updatedPositionSize = (T.position_size ?? 0) + qtyChg * price
    } else {
      const cur = T.quantity ?? 0
      if (qtyChg > cur) {
        showError(t('tradeList.quickErrors.reduceTooMuch'))
        return
      }
      updatedQuantity = cur - qtyChg
      updatedPositionSize = (T.position_size ?? 0) - qtyChg * price
    }

    if (updatedQuantity < 0) {
      showError(t('tradeList.quickErrors.quantityNegative'))
      return
    }

    const actionWord = event_type === 'add' ? 'Added' : 'Reduced'
    const description = `${actionWord} on ${formatSentenceDate(partialForm.eventDate)} at ${price}`

    setSaving(true)
    try {
      const { error: insErr } = await supabase.from('trade_history').insert({
        trade_id: T.id,
        user_id: user.id,
        event_type,
        description,
        price,
        mood: null,
        metadata: {
          tag: event_type,
          quantity: qtyChg,
        },
        ...(partialForm.eventDate.trim()
          ? { created_at: new Date(partialForm.eventDate).toISOString() }
          : {}),
      })
      if (insErr) throw insErr

      // quantity/position_size/totals are updated atomically by apply_trade_totals_from_history_insert.
      // Only run a follow-up close when a reduce fully exits the position.
      const fullyClosedByReduce = kind === 'partialClose' && updatedQuantity === 0
      if (fullyClosedByReduce) {
        const exitDateIso = partialForm.eventDate.trim()
          ? new Date(partialForm.eventDate).toISOString()
          : new Date().toISOString()
        const { error: upErr } = await updateTradeQuantityAfterEvent(supabase, T.id, {
          eventType: event_type,
          updatedQuantity,
          updatedPositionSize,
          exitDateIso,
          exitPrice: price,
        })
        if (upErr) throw upErr
      }

      showSuccess(
        fullyClosedByReduce
          ? t('tradeList.quickSaved.fullExit')
          : kind === 'partialClose'
            ? t('tradeList.quickSaved.partialClose')
            : t('tradeList.quickSaved.partialAdd')
      )
      closeModal()
      onUpdated()
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : String(e))
    } finally {
      setSaving(false)
    }
  }

  const submitFullExit = async () => {
    if (!modal?.trade) return
    const exitDateRaw = fullExitForm.exitDate.trim()
    const exitPx = parseFloat(fullExitForm.exitPrice.replace(',', '.'))
    if (!exitDateRaw) {
      showError(t('tradeList.quickErrors.exitDateRequired'))
      return
    }
    if (!Number.isFinite(exitPx) || exitPx <= 0) {
      showError(t('tradeList.quickErrors.exitPriceInvalid'))
      return
    }
    const T = modal.trade
    setSaving(true)
    try {
      const { error } = await supabase
        .from('trades')
        .update({
          exit_date: new Date(exitDateRaw).toISOString(),
          exit_price: exitPx,
          quantity: 0,
          position_size: 0,
        })
        .eq('id', T.id)
      if (error) throw error
      showSuccess(t('tradeList.quickSaved.fullExit'))
      closeModal()
      onUpdated()
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : String(e))
    } finally {
      setSaving(false)
    }
  }

  const submitNote = async () => {
    if (!user || !modal) return
    const added = noteDraft.trim()
    if (!added) {
      showError(t('tradeList.quickErrors.noteEmpty'))
      return
    }
    setSaving(true)
    try {
      await insertTradeComment(supabase, {
        tradeId: modal.trade.id,
        userId: user.id,
        text: added,
        mood: null,
      })
      showSuccess(t('tradeList.quickSaved.comment'))
      closeModal()
      onUpdated()
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : String(e))
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteFromMenu = useCallback(async () => {
    setMenuAnchor(null)
    const confirmed = await showConfirm({
      title: t('tradeList.delete'),
      message: t('tradeList.deleteConfirm'),
      confirmText: t('tradeList.delete'),
      cancelText: t('buttons.cancel', { ns: 'common' }),
      confirmButtonStyle: 'danger',
    })
    if (!confirmed) return
    try {
      setDeleting(true)
      const { error } = await supabase.from('trades').delete().eq('id', trade.id)
      if (error) throw error
      showSuccess(t('tradeList.quickSaved.deleted'))
      onUpdated()
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : String(e))
    } finally {
      setDeleting(false)
    }
  }, [showConfirm, showError, showSuccess, t, trade.id, onUpdated])

  const menuItems = (
    <>
      <button
        type="button"
        role="menuitem"
        className="flex w-full px-3 py-2 text-left text-sm text-gray-800 hover:bg-gray-50"
        onClick={() => openAction('sltp')}
      >
        {t('tradeList.quickActions.updateSltp')}
      </button>
      <button
        type="button"
        role="menuitem"
        disabled={!isOpenPosition}
        className="flex w-full px-3 py-2 text-left text-sm text-gray-800 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
        onClick={() => isOpenPosition && openAction('partialClose')}
      >
        {t('tradeList.quickActions.partialClose')}
      </button>
      <button
        type="button"
        role="menuitem"
        disabled={!isOpenPosition}
        className="flex w-full px-3 py-2 text-left text-sm text-gray-800 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
        onClick={() => isOpenPosition && openAction('partialAdd')}
      >
        {t('tradeList.quickActions.partialAdd')}
      </button>
      <button
        type="button"
        role="menuitem"
        disabled={!isOpenPosition}
        className="flex w-full px-3 py-2 text-left text-sm text-gray-800 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
        onClick={() => isOpenPosition && openAction('fullExit')}
      >
        {t('tradeList.quickActions.fullExit')}
      </button>
      <button
        type="button"
        role="menuitem"
        className="flex w-full px-3 py-2 text-left text-sm text-gray-800 hover:bg-gray-50"
        onClick={() => openAction('note')}
      >
        {t('tradeList.quickActions.addNote')}
      </button>
      <button
        type="button"
        role="menuitem"
        className="flex w-full px-3 py-2 text-left text-sm text-gray-800 hover:bg-gray-50"
        onClick={() => {
          setMenuAnchor(null)
          if (onEditTrade) onEditTrade(trade)
          else navigate(`/app/edit-trade/${trade.id}`)
        }}
      >
        {t('tradeList.quickActions.fullEdit')}
      </button>
      {!trade.is_public && (
        <button
          type="button"
          role="menuitem"
          disabled={deleting}
          className="flex w-full px-3 py-2 text-left text-sm text-red-700 hover:bg-red-50 disabled:opacity-50"
          onClick={() => void handleDeleteFromMenu()}
        >
          {t('tradeList.quickActions.delete')}
        </button>
      )}
    </>
  )

  const menuPos =
    menuAnchor && typeof window !== 'undefined' ? computeMenuPosition(menuAnchor.rect) : { top: 0, left: 0 }

  const modalPortal =
    modal &&
    typeof document !== 'undefined' &&
    createPortal(
      <div
        className="fixed inset-0 z-[206] overflow-y-auto overscroll-contain bg-black/50"
        role="presentation"
        onClick={() => !saving && closeModal()}
      >
        <div className="flex min-h-screen items-center justify-center p-4 py-10">
        <div
          role="dialog"
          aria-modal
          className="my-auto w-full max-w-sm rounded-2xl border border-gray-200 bg-white p-5 shadow-xl max-h-[calc(100vh-5rem)] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {modal.kind === 'sltp' && (
            <>
              <h3 className="mb-3 text-lg font-semibold text-gray-900">{t('tradeList.quickModalTitles.sltp')}</h3>
              <div className="space-y-3">
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-600">{t('tradeList.sl')}</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={sltpDraft.sl}
                    onChange={(e) => setSltpDraft((p) => ({ ...p, sl: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-600">{t('tradeList.tp')}</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={sltpDraft.tp}
                    onChange={(e) => setSltpDraft((p) => ({ ...p, tp: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>
              </div>
              <div className="mt-4 flex justify-end gap-2">
                <button
                  type="button"
                  className="rounded-lg px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100"
                  onClick={() => !saving && closeModal()}
                >
                  {t('buttons.cancel', { ns: 'common' })}
                </button>
                <button
                  type="button"
                  disabled={saving}
                  className="rounded-lg bg-orange-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-orange-600 disabled:opacity-50"
                  onClick={() => void submitSltp()}
                >
                  {saving ? '…' : t('buttons.save', { ns: 'common' })}
                </button>
              </div>
            </>
          )}

          {(modal.kind === 'partialClose' || modal.kind === 'partialAdd') && (
            <>
              <h3 className="mb-3 text-lg font-semibold text-gray-900">
                {modal.kind === 'partialClose'
                  ? t('tradeList.quickModalTitles.partialClose')
                  : t('tradeList.quickModalTitles.partialAdd')}
              </h3>
              <div className="space-y-3">
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-600">
                    {t('tradeList.quickFields.quantity')}
                  </label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={partialForm.qty}
                    onChange={(e) => setPartialForm((p) => ({ ...p, qty: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-600">
                    {t('tradeList.price')}
                  </label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={partialForm.price}
                    onChange={(e) => setPartialForm((p) => ({ ...p, price: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-600">
                    {t('tradeList.quickFields.eventDate')}
                  </label>
                  <input
                    type="datetime-local"
                    value={partialForm.eventDate}
                    onChange={(e) => setPartialForm((p) => ({ ...p, eventDate: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>
              </div>
              <div className="mt-4 flex justify-end gap-2">
                <button
                  type="button"
                  className="rounded-lg px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100"
                  onClick={() => !saving && closeModal()}
                >
                  {t('buttons.cancel', { ns: 'common' })}
                </button>
                <button
                  type="button"
                  disabled={saving}
                  className="rounded-lg bg-orange-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-orange-600 disabled:opacity-50"
                  onClick={() =>
                    void submitPartialEvent(modal.kind === 'partialAdd' ? 'partialAdd' : 'partialClose')
                  }
                >
                  {saving ? '…' : t('buttons.save', { ns: 'common' })}
                </button>
              </div>
            </>
          )}

          {modal.kind === 'fullExit' && (
            <>
              <h3 className="mb-3 text-lg font-semibold text-gray-900">{t('tradeList.quickModalTitles.fullExit')}</h3>
              <p className="mb-3 text-sm text-gray-500">{t('tradeList.quickFields.fullExitHint')}</p>
              <div className="space-y-3">
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-600">{t('tradeList.price')}</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={fullExitForm.exitPrice}
                    onChange={(e) => setFullExitForm((p) => ({ ...p, exitPrice: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-600">{t('tradeList.quickFields.exitDate')}</label>
                  <input
                    type="datetime-local"
                    value={fullExitForm.exitDate}
                    onChange={(e) => setFullExitForm((p) => ({ ...p, exitDate: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>
              </div>
              <div className="mt-4 flex justify-end gap-2">
                <button
                  type="button"
                  className="rounded-lg px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100"
                  onClick={() => !saving && closeModal()}
                >
                  {t('buttons.cancel', { ns: 'common' })}
                </button>
                <button
                  type="button"
                  disabled={saving}
                  className="rounded-lg bg-orange-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-orange-600 disabled:opacity-50"
                  onClick={() => void submitFullExit()}
                >
                  {saving ? '…' : t('tradeList.quickFields.confirmFullExit')}
                </button>
              </div>
            </>
          )}

          {modal.kind === 'note' && (
            <>
              <h3 className="mb-3 text-lg font-semibold text-gray-900">{t('tradeList.quickModalTitles.note')}</h3>
              <textarea
                value={noteDraft}
                onChange={(e) => setNoteDraft(e.target.value)}
                rows={4}
                placeholder={t('tradeList.quickFields.notePlaceholder')}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
              <div className="mt-4 flex justify-end gap-2">
                <button
                  type="button"
                  className="rounded-lg px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100"
                  onClick={() => !saving && closeModal()}
                >
                  {t('buttons.cancel', { ns: 'common' })}
                </button>
                <button
                  type="button"
                  disabled={saving}
                  className="rounded-lg bg-orange-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-orange-600 disabled:opacity-50"
                  onClick={() => void submitNote()}
                >
                  {saving ? '…' : t('tradeList.quickFields.appendNote')}
                </button>
              </div>
            </>
          )}
        </div>
        </div>
      </div>,
      document.body
    )

  if (directAction) {
    return modalPortal ?? null
  }

  return (
    <>
      <div className="relative inline-flex">
        <button
          type="button"
          title={t('tradeList.quickActions.menuTitle')}
          disabled={deleting}
          className="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg border border-transparent text-gray-600 hover:bg-gray-100 hover:text-gray-900 disabled:opacity-50"
          onClick={(e) => {
            e.stopPropagation()
            const rect = e.currentTarget.getBoundingClientRect()
            setMenuAnchor((prev) => (prev ? null : { rect }))
          }}
          aria-expanded={Boolean(menuAnchor)}
          aria-haspopup="menu"
        >
          <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
            <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
          </svg>
        </button>
      </div>

      {menuAnchor &&
        typeof document !== 'undefined' &&
        createPortal(
          <>
            <button
              type="button"
              className="fixed inset-0 z-[204] cursor-default bg-transparent"
              aria-label={t('tradeList.quickActions.dismissOverlay')}
              onClick={(e) => {
                e.stopPropagation()
                setMenuAnchor(null)
              }}
            />
            <div
              role="menu"
              className="fixed z-[205] max-h-[min(380px,calc(100vh-16px))] min-w-[228px] overflow-y-auto overscroll-contain rounded-lg border border-gray-200 bg-white py-1 shadow-xl"
              style={{ top: menuPos.top, left: menuPos.left }}
              onClick={(e) => e.stopPropagation()}
            >
              {menuItems}
            </div>
          </>,
          document.body
        )}

      {modalPortal}
    </>
  )
}
